import PropTypes from "prop-types";
import { Group } from "@mantine/core";
import { HiOutlineFunnel } from "react-icons/hi2";
import CustomSearchBar from "../../ui/CustomSearchBar/CustomSearchBar";
import CustomSelect from "../../ui/CustomSelect/CustomSelect";
import CustomDateRangePicker from "../../ui/CustomDateRangePicker/CustomDateRangePicker";
import CustomExportButton from "../../ui/CustomExportButton/CustomExportButton";
import ColumnVisibilitySelector from "../../ui/ColumnVisibilitySelector/ColumnVisibilitySelector";
import { CustomComingSoonCard } from "../../ui/CustomComingSoonCard/CustomComingSoonCard";
import useColumnVisibilityStore, {
  selectVisibleColumns,
} from "../../../stores/columnVisibilityStore";
import { parseDateRange } from "../../Transactions/utils/transactionFormatters";
import { optionsFilterByLabel } from "../../../utils/optionsFilter";
import useBulkUploadStatusOptions from "../hooks/useBulkUploadStatusOptions";

const DEFAULT_COLUMN_KEYS = ["batchId", "status", "paymentChannel"];

const COLUMN_META = [
  { key: "batchId", label: "Batch ID" },
  { key: "status", label: "Status" },
  { key: "paymentChannel", label: "Channel" },
  { key: "recipientCount", label: "Recipients" },
  { key: "amount", label: "Amount" },
  { key: "charges", label: "Charges" },
  { key: "totalAmount", label: "Total" },
];

const BulkUploadsFilters = ({
  filters,
  merchantProps,
  exportProps,
  disabled = false,
  isSuperAdmin = false,
}) => {
  const {
    searchQuery,
    setSearchQuery,
    dateRange,
    setDateRange,
    status,
    setStatus,
  } = filters;
  const { handleExport } = exportProps;
  const {
    merchantId,
    setMerchantId,
    options: merchantOptions,
    isLoading: merchantOptionsLoading,
  } = merchantProps ?? {};

  const {
    optionsWithAll: statusOptions,
    isLoading: statusOptionsLoading,
    isError: statusOptionsError,
    hasOptions: hasStatusOptions,
  } = useBulkUploadStatusOptions();

  let statusSelectTitle = "";
  if (statusOptionsError) {
    statusSelectTitle = "Cannot select: failed to load statuses";
  } else if (!statusOptionsLoading && !hasStatusOptions) {
    statusSelectTitle = "Cannot select: no statuses available";
  }

  const visibleColumnKeys = useColumnVisibilityStore(
    selectVisibleColumns("bulkUploads"),
  );
  const setVisibleColumns = useColumnVisibilityStore(
    (s) => s.setVisibleColumns,
  );

  return (
    <Group align="flex-start" justify="space-between" wrap="wrap">
      <Group align="flex-start" wrap="wrap">
        <CustomSearchBar
          placeholder="Search by Batch ID, channel, amount"
          onSearchChange={setSearchQuery}
          value={searchQuery}
          debounceDelay={600}
          size="sm"
          inputStyle={{ width: "clamp(200px, 40vw, 360px)", minWidth: 120 }}
          style={{ flex: "1 1 auto" }}
          disabled={disabled}
        />

        {isSuperAdmin && (
          <CustomSelect
            data={merchantOptions ?? []}
            value={merchantId ?? ""}
            onChange={(v) => setMerchantId?.(v || "")}
            searchable
            comboboxProps={{ withinPortal: false }}
            filter={optionsFilterByLabel}
            placeholder="All Merchants"
            size="sm"
            style={{ width: 200 }}
            disabled={disabled || merchantOptionsLoading}
          />
        )}

        <CustomSelect
          data={statusOptions}
          value={status}
          onChange={(v) => setStatus(v || "all")}
          leftSection={<HiOutlineFunnel color="gray" size={14} />}
          searchable
          comboboxProps={{ withinPortal: false }}
          filter={optionsFilterByLabel}
          placeholder="All Statuses"
          title={statusSelectTitle}
          size="sm"
          style={{ width: 160 }}
          disabled={
            disabled ||
            statusOptionsLoading ||
            statusOptionsError ||
            !hasStatusOptions
          }
        />

        <CustomDateRangePicker
          value={parseDateRange(dateRange)}
          onChange={setDateRange}
          size="sm"
          disabled={disabled}
        />
      </Group>

      <Group gap="xs" align="flex-start">
        <CustomComingSoonCard>
          <CustomExportButton
            label="Export History"
            filename="bulk_uploads"
            showLoadingOverlay={false}
            disabled={disabled}
            customExportHandlers={{
              pdf: handleExport,
              csv: () => {},
              excel: () => {},
            }}
            exportConfig={{
              csv: { disabled: true },
              excel: { disabled: true },
            }}
          />
        </CustomComingSoonCard>
        <ColumnVisibilitySelector
          columns={COLUMN_META}
          defaultColumnKeys={DEFAULT_COLUMN_KEYS}
          visibleColumnKeys={visibleColumnKeys}
          onChange={(keys) => setVisibleColumns("bulkUploads", keys)}
          buttonProps={{ disabled }}
        />
      </Group>
    </Group>
  );
};

BulkUploadsFilters.propTypes = {
  filters: PropTypes.shape({
    searchQuery: PropTypes.string,
    setSearchQuery: PropTypes.func,
    dateRange: PropTypes.shape({
      startDate: PropTypes.string,
      endDate: PropTypes.string,
    }),
    setDateRange: PropTypes.func,
    status: PropTypes.string,
    setStatus: PropTypes.func,
  }).isRequired,
  merchantProps: PropTypes.shape({
    merchantId: PropTypes.string,
    setMerchantId: PropTypes.func,
    options: PropTypes.array,
    isLoading: PropTypes.bool,
    isError: PropTypes.bool,
  }),
  exportProps: PropTypes.shape({
    handleExport: PropTypes.func,
    isExporting: PropTypes.bool,
  }).isRequired,
  disabled: PropTypes.bool,
  isSuperAdmin: PropTypes.bool,
};

export default BulkUploadsFilters;
